import type { Agent } from "@/types/aiToolkit";

export type AgentStrengthSectionId =
  | "agent-section-identity"
  | "agent-section-instructions"
  | "agent-section-knowledge"
  | "agent-section-advanced";

export type AgentStrengthNudge = {
  id: string;
  label: string;
  sectionId: AgentStrengthSectionId;
  weight: number;
  earned: boolean;
};

export type AgentStrengthResult = {
  score: number;
  nudges: AgentStrengthNudge[];
};

const WEIGHTS = {
  identity: 15,
  instructions: 25,
  knowledge: 20,
  skills: 15,
  toolsScopes: 10,
  output: 10,
  profileModel: 5,
} as const;

function scoreIdentity(agent: Partial<Agent>): number {
  let s = 0;
  if (agent.name?.trim()) s += 0.5;
  if (agent.description?.trim()) s += 0.5;
  return s;
}

function scoreInstructions(agent: Partial<Agent>): number {
  let s = 0;
  const promptLen = agent.persona?.systemPrompt?.length ?? 0;
  if (promptLen >= 50) s += 0.6;
  else if (promptLen > 0) s += 0.3;
  if (agent.persona?.role?.trim()) s += 0.2;
  if ((agent.persona?.expertise?.length ?? 0) > 0) s += 0.2;
  return Math.min(1, s);
}

function scoreKnowledge(agent: Partial<Agent>): number {
  const count =
    (agent.linkedSourceIds?.length ?? 0) + (agent.trainingDocumentIds?.length ?? 0);
  if (count >= 3) return 1;
  if (count >= 1) return 0.6;
  return 0;
}

function scoreSkills(agent: Partial<Agent>): number {
  if ((agent.customInstructions?.trim()?.length ?? 0) >= 30) return 1;
  if ((agent.attachedSkillIds?.length ?? 0) > 0) return 1;
  if (agent.customInstructions?.trim()) return 0.5;
  return 0;
}

function scoreToolsScopes(agent: Partial<Agent>): number {
  const hasScopes = (agent.integrationScopes?.length ?? 0) > 0;
  const hasTools = (agent.allowedToolIds?.length ?? 0) > 0;
  if (hasScopes && hasTools) return 1;
  if (hasScopes || hasTools) return 0.6;
  return 0;
}

function scoreOutput(agent: Partial<Agent>): number {
  const out = agent.outputRequirements;
  if (!out) return 0;
  let s = 0;
  if (out.instructions?.trim()) s += 0.5;
  if ((out.requiredSections?.length ?? 0) > 0) s += 0.3;
  if (out.maxLengthHint?.trim()) s += 0.2;
  return Math.min(1, s);
}

function scoreProfileModel(agent: Partial<Agent>): number {
  if (agent.workerProfileId || agent.preferredModel) return 1;
  return 0;
}

export function computeAgentStrength(agent: Partial<Agent>): AgentStrengthResult {
  const categories: AgentStrengthNudge[] = [
    {
      id: "identity",
      label: "Add a name and description",
      sectionId: "agent-section-identity",
      weight: WEIGHTS.identity,
      earned: scoreIdentity(agent) >= 1,
    },
    {
      id: "instructions",
      label: "Write a detailed system prompt",
      sectionId: "agent-section-instructions",
      weight: WEIGHTS.instructions,
      earned: scoreInstructions(agent) >= 0.8,
    },
    {
      id: "knowledge",
      label: "Attach knowledge sources or documents",
      sectionId: "agent-section-knowledge",
      weight: WEIGHTS.knowledge,
      earned: scoreKnowledge(agent) > 0,
    },
    {
      id: "skills",
      label: "Add skills or custom instructions",
      sectionId: "agent-section-advanced",
      weight: WEIGHTS.skills,
      earned: scoreSkills(agent) > 0,
    },
    {
      id: "toolsScopes",
      label: "Set integration scopes or allowed tools",
      sectionId: "agent-section-advanced",
      weight: WEIGHTS.toolsScopes,
      earned: scoreToolsScopes(agent) > 0,
    },
    {
      id: "output",
      label: "Define output requirements",
      sectionId: "agent-section-advanced",
      weight: WEIGHTS.output,
      earned: scoreOutput(agent) > 0,
    },
    {
      id: "profileModel",
      label: "Choose a worker profile or model",
      sectionId: "agent-section-advanced",
      weight: WEIGHTS.profileModel,
      earned: scoreProfileModel(agent) > 0,
    },
  ];

  const score = Math.round(
    scoreIdentity(agent) * WEIGHTS.identity +
      scoreInstructions(agent) * WEIGHTS.instructions +
      scoreKnowledge(agent) * WEIGHTS.knowledge +
      scoreSkills(agent) * WEIGHTS.skills +
      scoreToolsScopes(agent) * WEIGHTS.toolsScopes +
      scoreOutput(agent) * WEIGHTS.output +
      scoreProfileModel(agent) * WEIGHTS.profileModel
  );

  const missing = categories
    .filter((c) => !c.earned)
    .sort((a, b) => b.weight - a.weight);

  return {
    score: Math.min(100, Math.max(0, score)),
    nudges: missing,
  };
}
